import * as Accordion from "@radix-ui/react-accordion";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import styles from "../live-seo-page.module.css";

type FaqItem = {
	question: string;
	answers: string[];
};

type FaqsLocalization = {
	title: string;
	heading: string;
	subheading: string;
	faqs: FaqItem[];
};

const englishFaqItems: FaqItem[] = [
	{
		question: "How much does treatment cost?",
		answers: [
			"The cost of orthodontic treatment depends on many factors, including the severity of the problem, how complex the problem is, and how long treatment lasts.",
			"Dr. Garcia-Paul will be glad to discuss the cost of treatment and your financing options with you before treatment begins.",
			"The good news is that braces are more affordable today than ever. Our staff also works with insurance companies and offers payment plans that meet your family's budget.",
		],
	},
	{
		question: "Why is treatment so important?",
		answers: [
			"Crooked and crowded teeth are hard to clean and maintain. A bad bite can also cause abnormal wear of tooth surfaces, difficulty in chewing and/or speaking, and excess stress on supporting bone and gum tissue.",
			"Without treatment, many problems become worse and can require additional dental care later in life.",
		],
	},
	{
		question: "What causes orthodontic problems?",
		answers: [
			"Most orthodontic problems are inherited. Examples of these genetic problems are crowding, spacing, protrusion, extra or missing teeth and some jaw growth problems.",
			"Other oral problems are caused by thumb- or finger-sucking, dental disease, accidents, the early or late loss of baby teeth, or other causes.",
		],
	},
	{
		question: "How long does treatment take?",
		answers: [
			"Patients who need orthodontic treatment generally have one to three years of active treatment.",
			"This can be determined through a free consultation at our Miami, FL office.",
		],
	},
	{
		question: "Why choose an orthodontist over aligners that come in the mail?",
		answers: [
			"Selecting an orthodontist in Miami, FL who can help you achieve your smile dreams is important. Orthodontists are dental specialists who diagnose, prevent and treat dental and facial irregularities.",
			'They receive an additional two to three years of specialized education beyond dental school to learn the proper way to align and straighten teeth. Only those with this formal education may call themselves "orthodontists," and only orthodontists may be members of the American Association of Orthodontists (AAO).',
			"Dr. Garcia-Paul from Miami Orthodontic Specialists has achieved this specialization and is an active member in AAO.",
		],
	},
	{
		question: "When should children get an orthodontic check-up?",
		answers: [
			"The American Association of Orthodontists recommends an orthodontic screening at age seven. By this age, most children have several permanent teeth that have erupted, letting us evaluate their orthodontic needs.",
			"By this age, Dr. Garcia-Paul can spot subtle problems with jaw growth and emerging teeth while some baby teeth are still present. That's important, because some orthodontic problems may be easier to correct if they're found early. Most orthodontic patients begin active braces treatment between ages 9 and 14.",
		],
	},
	{
		question: "Can adults benefit from orthodontic treatment?",
		answers: [
			"Orthodontic treatment can be successful at almost any age. In fact, about one in every four orthodontic patients today is over age 18.",
			"Thanks to today's smaller, less visible and more comfortable orthodontic appliances, including metal braces, ceramic braces, and Invisalign, adults find treatment appealing.",
		],
	},
	{
		question: "What is my role in treatment?",
		answers: [
			"Orthodontic treatment is a partnership of the doctor and patient. Dr. Garcia-Paul provides custom-made fixed or removable appliances that use gentle pressure to move teeth into their proper positions.",
			"Your job is to follow Dr. Garcia-Paul's instructions, keep scheduled orthodontic appointments and maintaining excellent oral hygiene to get the best results.",
			"You will also need to see your general dentist as recommended to continue your general dental care.",
		],
	},
	{
		question:
			"I had braces as a teen, and now as an adult my teeth are out of place. What can I do?",
		answers: [
			'If you notice an unwanted change in your smile or bite, contact Miami Orthodontic Specialists at 305-407-9000 for information. An orthodontic "tune up" may be necessary to regain proper alignment.',
		],
	},
	{
		question: "How long do I have to wear retainers after treatment?",
		answers: [
			"Dr. Garcia-Paul will recommend how long to continue wearing your retainers, whether they are removable (the kind you put in and take out) or fixed (bonded behind your teeth). Wearing your retainers as prescribed is the best way to keep your teeth from moving after your orthodontic treatment.",
			"There are many reasons teeth may move following orthodontic treatment. Teeth are not set in concrete, they are in bone. Because bone around your teeth is continually changing (breaking down and rebuilding), your teeth may shift after your braces are removed.",
			"By wearing your retainers, your teeth are more likely to remain where Dr. Garcia-Paul has placed them through braces treatment.",
		],
	},
	{
		question: "Why do some teeth shift?",
		answers: [
			"The small changes that happen after braces are removed are due to a settling in as you use your teeth for biting and chewing. The more serious and unwanted changes may be traced to genetics or later-than-normal growth, which is unpredictable.",
			"Movement is most common in lower front teeth. This is particularly true if the teeth were extremely crowded prior to treatment. Changes in tooth position are a lifelong and naturally occurring phenomenon.",
			"The best way to keep your teeth from shifting is to wear your retainers as prescribed. If you notice movement after your braces are removed, please contact Miami Orthodontic Specialists.",
		],
	},
	{
		question: "What should my long-term expectations be?",
		answers: [
			"The fact is that throughout your lifetime, even though you have had orthodontic treatment, you can expect changes in tooth position. Many factors at work may cause teeth to shift. Such changes are different for everyone and most of the time they are hardly noticeable.",
			"However, on occasion changes can occur that are disappointing to both the patient and Dr. Garcia-Paul. Changes in tooth position are not a failure of your orthodontic treatment. They are a natural process. We expect changes in our bodies as we grow older, and teeth are no exception.",
			"To help control and limit these changes, retainers are prescribed after your braces are removed.",
		],
	},
	{
		question: "Can retainers be worn too long?",
		answers: [
			"No. Retainers stabilize and preserve the alignment of teeth and jaws that orthodontic treatment achieved. Many people wear removable retainers nightly for the rest of their lives. Ask your orthodontist for guidance about your long-term retainer use.",
		],
	},
	{
		question: "Will wisdom teeth cause teeth to shift?",
		answers: [
			"Not necessarily. Research suggests that wisdom teeth don't always cause teeth to shift.",
			"In most cases, wisdom teeth are removed for general dental health reasons rather than for orthodontic health. Your family dentist or Dr. Garcia-Paul can help determine whether or not your wisdom teeth need to be removed.",
		],
	},
];

const spanishFaqItems: FaqItem[] = [
	{
		question: "¿Cuánto cuesta el tratamiento?",
		answers: [
			"El costo del tratamiento de ortodoncia depende de muchos factores, incluida la gravedad del problema, su complejidad y la duración del tratamiento.",
			"La Dra. Garcia-Paul con gusto hablará contigo sobre el costo del tratamiento y tus opciones de financiamiento antes de comenzar.",
			"La buena noticia es que los brackets son más accesibles hoy que nunca. Nuestro equipo también trabaja con compañías de seguro y ofrece planes de pago que se ajustan al presupuesto de tu familia.",
		],
	},
	{
		question: "¿Por qué es tan importante el tratamiento?",
		answers: [
			"Los dientes torcidos o apiñados son difíciles de limpiar y mantener. Una mala mordida también puede causar desgaste anormal de las superficies dentales, dificultad para masticar o hablar y exceso de presión sobre el hueso y las encías.",
			"Sin tratamiento, muchos problemas empeoran y pueden requerir atención dental adicional más adelante.",
		],
	},
	{
		question: "¿Qué causa los problemas de ortodoncia?",
		answers: [
			"La mayoría de los problemas de ortodoncia son hereditarios. Algunos ejemplos son el apiñamiento, los espacios, la protrusión, dientes adicionales o faltantes y ciertos problemas de crecimiento mandibular.",
			"Otros problemas bucales pueden ser causados por chuparse el dedo, enfermedad dental, accidentes, pérdida temprana o tardía de dientes de leche u otras causas.",
		],
	},
	{
		question: "¿Cuánto dura el tratamiento?",
		answers: [
			"Los pacientes que necesitan tratamiento de ortodoncia generalmente requieren de uno a tres años de tratamiento activo.",
			"Esto se puede determinar durante una consulta gratuita en nuestra oficina de Miami, FL.",
		],
	},
	{
		question:
			"¿Por qué elegir un ortodoncista en vez de alineadores por correo?",
		answers: [
			"Elegir un ortodoncista en Miami, FL que pueda ayudarte a lograr la sonrisa que deseas es importante. Los ortodoncistas son especialistas dentales que diagnostican, previenen y tratan irregularidades dentales y faciales.",
			"Reciben dos o tres años adicionales de educación especializada después de la escuela dental para aprender la forma correcta de alinear y enderezar los dientes. Solo quienes tienen esta educación formal pueden llamarse ortodoncistas, y solo los ortodoncistas pueden ser miembros de la American Association of Orthodontists (AAO).",
			"La Dra. Garcia-Paul de Miami Orthodontic Specialists ha logrado esta especialización y es miembro activo de la AAO.",
		],
	},
	{
		question: "¿Cuándo deben los niños tener una revisión de ortodoncia?",
		answers: [
			"La American Association of Orthodontists recomienda una evaluación de ortodoncia a los siete años. A esta edad, la mayoría de los niños ya tiene varios dientes permanentes, lo que nos permite evaluar sus necesidades.",
			"A esta edad, la Dra. Garcia-Paul puede detectar problemas sutiles en el crecimiento mandibular y en los dientes que están saliendo mientras aún hay dientes de leche presentes. Esto es importante porque algunos problemas pueden ser más fáciles de corregir si se detectan temprano. La mayoría de los pacientes comienzan el tratamiento activo con brackets entre los 9 y 14 años.",
		],
	},
	{
		question: "¿Pueden los adultos beneficiarse del tratamiento de ortodoncia?",
		answers: [
			"El tratamiento de ortodoncia puede tener éxito a casi cualquier edad. De hecho, aproximadamente uno de cada cuatro pacientes de ortodoncia hoy tiene más de 18 años.",
			"Gracias a los aparatos de ortodoncia actuales, más pequeños, discretos y cómodos, incluidos brackets metálicos, brackets cerámicos e Invisalign, muchos adultos encuentran atractivo el tratamiento.",
		],
	},
	{
		question: "¿Cuál es mi papel en el tratamiento?",
		answers: [
			"El tratamiento de ortodoncia es una colaboración entre el doctor y el paciente. La Dra. Garcia-Paul proporciona aparatos fijos o removibles personalizados que usan presión suave para mover los dientes a su posición correcta.",
			"Tu trabajo es seguir las instrucciones de la Dra. Garcia-Paul, asistir a tus citas de ortodoncia programadas y mantener una excelente higiene oral para obtener los mejores resultados.",
			"También deberás visitar a tu dentista general según se recomiende para continuar con tu cuidado dental general.",
		],
	},
	{
		question:
			"Tuve brackets de adolescente y ahora, de adulto, mis dientes se movieron. ¿Qué puedo hacer?",
		answers: [
			"Si notas un cambio no deseado en tu sonrisa o mordida, comunícate con Miami Orthodontic Specialists al 305-407-9000 para obtener información. Puede ser necesario un ajuste de ortodoncia para recuperar la alineación adecuada.",
		],
	},
	{
		question: "¿Cuánto tiempo debo usar retenedores después del tratamiento?",
		answers: [
			"La Dra. Garcia-Paul recomendará cuánto tiempo debes seguir usando tus retenedores, ya sean removibles o fijos. Usarlos según lo indicado es la mejor forma de evitar que tus dientes se muevan después del tratamiento de ortodoncia.",
			"Hay muchas razones por las que los dientes pueden moverse después del tratamiento. Los dientes no están fijos en concreto; están en hueso. Debido a que el hueso alrededor de los dientes cambia continuamente, los dientes pueden desplazarse después de retirar los brackets.",
			"Al usar tus retenedores, es más probable que tus dientes permanezcan donde la Dra. Garcia-Paul los colocó durante el tratamiento.",
		],
	},
	{
		question: "¿Por qué algunos dientes se mueven?",
		answers: [
			"Los pequeños cambios que ocurren después de retirar los brackets se deben al asentamiento natural al usar los dientes para morder y masticar. Los cambios más serios o no deseados pueden deberse a la genética o a crecimiento tardío, que es impredecible.",
			"El movimiento es más común en los dientes frontales inferiores, especialmente si estaban muy apiñados antes del tratamiento. Los cambios en la posición dental son un fenómeno natural de por vida.",
			"La mejor forma de evitar que los dientes se muevan es usar los retenedores según lo indicado. Si notas movimiento después de retirar los brackets, comunícate con Miami Orthodontic Specialists.",
		],
	},
	{
		question: "¿Cuáles deben ser mis expectativas a largo plazo?",
		answers: [
			"La realidad es que durante toda la vida, incluso después de un tratamiento de ortodoncia, puedes esperar cambios en la posición dental. Muchos factores pueden hacer que los dientes se muevan. Estos cambios son diferentes para cada persona y, la mayoría de las veces, apenas se notan.",
			"Sin embargo, en ocasiones pueden ocurrir cambios que decepcionan tanto al paciente como a la Dra. Garcia-Paul. Los cambios en la posición dental no significan que el tratamiento haya fallado; son un proceso natural. Esperamos cambios en nuestro cuerpo al envejecer, y los dientes no son la excepción.",
			"Para ayudar a controlar y limitar estos cambios, se recetan retenedores después de retirar los brackets.",
		],
	},
	{
		question: "¿Se pueden usar retenedores por demasiado tiempo?",
		answers: [
			"No. Los retenedores estabilizan y preservan la alineación de dientes y mandíbulas lograda con el tratamiento de ortodoncia. Muchas personas usan retenedores removibles todas las noches por el resto de su vida. Pide orientación a tu ortodoncista sobre el uso de retenedores a largo plazo.",
		],
	},
	{
		question: "¿Las muelas del juicio hacen que los dientes se muevan?",
		answers: [
			"No necesariamente. Las investigaciones sugieren que las muelas del juicio no siempre causan que los dientes se muevan.",
			"En la mayoría de los casos, las muelas del juicio se extraen por razones de salud dental general, no por salud ortodóntica. Tu dentista familiar o la Dra. Garcia-Paul pueden ayudarte a determinar si deben extraerse.",
		],
	},
];

const faqsLocalization = {
	[Locale.EN]: {
		title: "Ortho FAQs",
		heading: "Miami Orthodontic Specialists Answers",
		subheading: "Your Common Questions About Orthodontics",
		faqs: englishFaqItems,
	},
	[Locale.ES]: {
		title: "Preguntas frecuentes de ortodoncia",
		heading: "Respuestas de Miami Orthodontic Specialists",
		subheading: "Tus preguntas comunes sobre ortodoncia",
		faqs: spanishFaqItems,
	},
} satisfies Record<Locale, FaqsLocalization>;

export function FaqsPage() {
	const getLocalization = useLocalization(faqsLocalization);

	return (
		<Flex direction="column">
			<HeroStyle2
				showFullImageOnMobile
				heroImageUrl="/miami-ortho-6.jpg"
				title={getLocalization("title")}
				backgroundPosition="center"
			/>
			<Box className={styles.faqsBand}>
				<Box className={styles.faqsInner}>
					<Heading as="h2" className={styles.faqsHeading}>
						{getLocalization("heading")}
					</Heading>
					<Heading as="h3" className={styles.faqsSubheading}>
						{getLocalization("subheading")}
					</Heading>
					<Accordion.Root className={styles.faqsList} type="single" collapsible>
						{getLocalization("faqs").map((item, index) => (
							<Accordion.Item
								className={styles.faqItem}
								key={item.question}
								value={`faq-${index}`}
							>
								<Accordion.Header className={styles.faqHeader}>
									<Accordion.Trigger className={styles.faqQuestion}>
										<span>{item.question}</span>
										<span className={styles.faqIcon} aria-hidden="true">
											+
										</span>
									</Accordion.Trigger>
								</Accordion.Header>
								<Accordion.Content forceMount className={styles.faqAnswer}>
									<Box className={styles.faqAnswerInner}>
										{item.answers.map((answer) => (
											<Text
												as="p"
												className={styles.faqAnswerText}
												key={answer}
											>
												{answer}
											</Text>
										))}
									</Box>
								</Accordion.Content>
							</Accordion.Item>
						))}
					</Accordion.Root>
				</Box>
			</Box>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
